import React, { useState, useRef } from 'react';
import { 
  generateCertificates, 
  downloadCertificateExcelTemplate, 
  downloadCertificateDocxTemplate,
  uploadCertificateTemplate 
} from '../../services/api';
import './CertificatesPage.css';

export const CertificatesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'generate' | 'template'>('generate');
  
  // Generate State
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const excelInputRef = useRef<HTMLInputElement>(null);

  // Template Upload State
  const [templateFile, setTemplateFile] = useState<File | null>(null);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const [templateSuccess, setTemplateSuccess] = useState<string | null>(null);
  const templateInputRef = useRef<HTMLInputElement>(null);

  // Drag & Drop for Excel
  const handleExcelDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleExcelSelect(e.dataTransfer.files[0]);
    }
  };

  const handleExcelSelect = (selectedFile: File) => {
    if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
      setError('Sadece .xlsx veya .xls formatında Excel dosyaları kabul edilmektedir.');
      return;
    }
    setExcelFile(selectedFile);
    setError(null);
    setSuccess(false);
  };

  // Drag & Drop for Word Template
  const handleTemplateDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleTemplateSelect(e.dataTransfer.files[0]);
    }
  };

  const handleTemplateSelect = (selectedFile: File) => {
    if (!selectedFile.name.endsWith('.docx')) {
      setTemplateError('Sadece .docx formatında Word dosyaları kabul edilmektedir.');
      return;
    }
    setTemplateFile(selectedFile);
    setTemplateError(null);
    setTemplateSuccess(null);
  };

  // Actions
  const handleGenerate = async () => {
    if (!excelFile) {
      setError('Lütfen Excel listesini seçin.');
      return;
    }

    setError(null);
    setSuccess(false);
    setLoading(true);

    try {
      const blob = await generateCertificates(excelFile);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sertifikalar.zip';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Sertifikalar oluşturulurken bir hata oluştu. Şablonun yüklü olduğundan emin olun.');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadTemplate = async () => {
    if (!templateFile) {
      setTemplateError('Lütfen bir Word (.docx) şablon dosyası seçin.');
      return;
    }

    setTemplateError(null);
    setTemplateSuccess(null);
    setUploadingTemplate(true);

    try {
      const res = await uploadCertificateTemplate(templateFile);
      setTemplateSuccess(res.message || 'Sertifika şablonu başarıyla güncellendi.');
      setTemplateFile(null);
    } catch (err: any) {
      setTemplateError(err.message || 'Şablon yüklenirken bir hata oluştu.');
    } finally {
      setUploadingTemplate(false);
    }
  };

  const handleDownloadExcelTemplate = async () => {
    try {
      const blob = await downloadCertificateExcelTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sertifika_sablonu.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      setError('Örnek Excel şablonu indirilemedi.');
    }
  };

  const handleDownloadDocxTemplate = async () => {
    try {
      const blob = await downloadCertificateDocxTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sertifika_sablonu.docx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      setTemplateError('Aktif Word şablonu indirilemedi.');
    }
  };

  return (
    <div className="cert-page">
      {/* Header */}
      <div className="cert-header">
        <div className="cert-header-content">
          <div className="cert-header-icon">📜</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 className="cert-title">Sertifika Yönetimi & Otomatik Üretim</h1>
            <p className="cert-subtitle">
              Excel listenizi yükleyerek Word şablonu üzerinden her çalışan için otomatik sertifika üretin.
            </p>
          </div>
          <div className="cert-header-actions">
            <button 
              type="button"
              className="cert-btn-header"
              onClick={handleDownloadExcelTemplate}
              title="Örnek katılımcı Excel dosyasını indir"
            >
              <span>📥</span> Örnek Excel
            </button>
            <button 
              type="button"
              className="cert-btn-header"
              onClick={handleDownloadDocxTemplate}
              title="Sistemde kayıtlı Word şablonunu indir"
            >
              <span>📝</span> Aktif Şablon
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="cert-tabs">
        <button
          type="button"
          onClick={() => setActiveTab('generate')}
          className={`cert-tab-btn ${activeTab === 'generate' ? 'active-generate' : ''}`}
        >
          <span>🚀</span> Toplu Sertifika Üretimi
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('template')}
          className={`cert-tab-btn ${activeTab === 'template' ? 'active-template' : ''}`}
        >
          <span>⚙️</span> Word Şablonunu Güncelle (.docx)
        </button>
      </div>

      {/* TAB 1: GENERATE CERTIFICATES */}
      {activeTab === 'generate' && (
        <>
          <div className="cert-info-strip">
            <span style={{ fontSize: '1rem' }}>💡</span>
            <span>
              <strong>Nasıl Çalışır?</strong> Excel'deki sütunlar (<code>Ad Soyad</code>, <code>Görevi</code>, <code>Tarih</code>, <code>Eğitim Süresi</code> vb.) Word şablonuna işlenerek tüm çalışanlar için bağımsız belgeler üretilir ve tek ZIP halinde indirilir.
            </span>
          </div>

          <div className="cert-card">
            <div className="cert-card-header cert-card-header-generate">
              <h2>
                <span>📊</span> Excel İle Toplu Sertifika Üretimi
              </h2>
            </div>
            
            <div className="cert-card-body">
              {error && (
                <div className="cert-alert-error">
                  ⚠️ {error}
                </div>
              )}
              
              {success && (
                <div className="cert-alert-success">
                  ✅ Sertifikalar başarıyla üretildi ve ZIP paketi olarak indirildi.
                </div>
              )}

              <div 
                className="cert-dropzone"
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={handleExcelDrop}
                onClick={() => excelInputRef.current?.click()}
              >
                <input 
                  type="file" 
                  accept=".xlsx, .xls"
                  ref={excelInputRef}
                  style={{ display: 'none' }}
                  onChange={(e) => e.target.files && handleExcelSelect(e.target.files[0])}
                />
                <div className="cert-dropzone-icon">{excelFile ? '📊' : '📁'}</div>
                <div>
                  <h3 className="cert-dropzone-title">
                    {excelFile ? excelFile.name : 'Excel Dosyasını Sürükleyin veya Seçin'}
                  </h3>
                  {!excelFile && <p className="cert-dropzone-desc">Sadece .xlsx veya .xls formatı desteklenmektedir</p>}
                </div>
                {excelFile && (
                  <div className="cert-dropzone-change">
                    Değiştirmek için tıklayın veya yeni dosya sürükleyin
                  </div>
                )}
              </div>
            </div>

            <div className="cert-card-footer">
              <button 
                type="button"
                className="cert-btn-primary" 
                onClick={handleGenerate} 
                disabled={loading || !excelFile}
              >
                {loading ? '⏳ Üretiliyor...' : '🚀 Sertifikaları Üret ve İndir (ZIP)'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* TAB 2: TEMPLATE MANAGEMENT */}
      {activeTab === 'template' && (
        <>
          <div className="cert-info-strip">
            <span style={{ fontSize: '1rem' }}>📌</span>
            <span>
              <strong>Şablon Kılavuzu:</strong> Word (.docx) dosyanızda dilediğiniz tasarımı yapabilir, katılımcı ve eğitim alanlarına <code>&#123;&#123;ETIKET_ADI&#125;&#125;</code> yer tutucuları ekleyebilirsiniz.
            </span>
          </div>

          <div className="cert-card">
            <div className="cert-card-header cert-card-header-template">
              <h2>
                <span>📝</span> Yeni Word Şablonu (.docx) Yükle
              </h2>
            </div>
            
            <div className="cert-card-body">
              {templateError && (
                <div className="cert-alert-error">
                  ⚠️ {templateError}
                </div>
              )}
              
              {templateSuccess && (
                <div className="cert-alert-success">
                  ✅ {templateSuccess}
                </div>
              )}

              <div 
                className="cert-dropzone cert-dropzone-template"
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={handleTemplateDrop}
                onClick={() => templateInputRef.current?.click()}
              >
                <input 
                  type="file" 
                  accept=".docx"
                  ref={templateInputRef}
                  style={{ display: 'none' }}
                  onChange={(e) => e.target.files && handleTemplateSelect(e.target.files[0])}
                />
                <div className="cert-dropzone-icon">{templateFile ? '📝' : '📤'}</div>
                <div>
                  <h3 className="cert-dropzone-title">
                    {templateFile ? templateFile.name : 'Yeni Word (.docx) Şablonunu Seçin veya Sürükleyin'}
                  </h3>
                  {!templateFile && <p className="cert-dropzone-desc">Sadece Microsoft Word .docx formatı desteklenmektedir</p>}
                </div>
                {templateFile && (
                  <div className="cert-dropzone-change" style={{ color: '#7c3aed' }}>
                    Dosyayı değiştirmek için tıklayın
                  </div>
                )}
              </div>

              {/* Supported Tag Reference Guide */}
              <div className="cert-tags-box">
                <h4 className="cert-tags-title">
                  🏷️ Kullanılabilir Dinamik Etiketler:
                </h4>
                <div className="cert-tags-grid">
                  <div className="cert-tag-pill">
                    <code>&#123;&#123;AD_SOYAD&#125;&#125;</code> <span>Katılımcı</span>
                  </div>
                  <div className="cert-tag-pill">
                    <code>&#123;&#123;GOREVI&#125;&#125;</code> <span>Görevi</span>
                  </div>
                  <div className="cert-tag-pill">
                    <code>&#123;&#123;TARIH&#125;&#125;</code> <span>Tarih</span>
                  </div>
                  <div className="cert-tag-pill">
                    <code>&#123;&#123;EGITIM_SURESI&#125;&#125;</code> <span>Süre</span>
                  </div>
                  <div className="cert-tag-pill">
                    <code>&#123;&#123;GECERLILIK_SURESI&#125;&#125;</code> <span>Geçerlilik</span>
                  </div>
                  <div className="cert-tag-pill">
                    <code>&#123;&#123;EGITIM_KURUMU&#125;&#125;</code> <span>Kurum</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="cert-card-footer">
              <button 
                type="button"
                className="cert-btn-primary cert-btn-purple" 
                onClick={handleUploadTemplate} 
                disabled={uploadingTemplate || !templateFile}
              >
                {uploadingTemplate ? '⏳ Yükleniyor...' : '💾 Şablonu Kaydet ve Güncelle'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CertificatesPage;
